import type { AppCheck } from "firebase/app-check";

/**
 * START APP CHECK SO THAT reCAPTCHA CAN NEVER THROW ON THE PAGE. App and Book.
 *
 * THE ROOT CAUSE of Sentry issue LURA-BOOK-WEB-2, "no recaptcha" (3,317
 * events in a day, 2026-10-06). `initializeAppCheck` -> `_activate` ->
 * `ReCaptchaV3Provider.initialize` -> `initializeV3` (@firebase/app-check
 * 0.13.0, dist/esm/index.esm.js:1071) injects
 * https://www.google.com/recaptcha/api.js with an `onload` callback
 * (`loadReCAPTCHAV3Script`, :1177). That callback reads `self.grecaptcha`
 * and, when the script loaded but did not define it, does
 *
 *     throw new Error('no recaptcha');   // index.esm.js:1082, "it shouldn't happen"
 *
 * synchronously inside a script element's load handler. Nothing can catch
 * that: it is not a promise, so it reaches `window` as an uncaught ErrorEvent,
 * which our pre-start listener (mechanism `lura.early`) or the SDK's onerror
 * handler (`onerror`) reported as an unhandled Error on every page view. It
 * happens whenever api.js answers 200 without defining `grecaptcha`: Book's
 * e2e stub (tests/e2e/book/support/api.js, `stubGoogleDependencies`, an
 * EMPTY body), and in the field a content blocker's no-op surrogate. A
 * BLOCKED script (aborted) fires `error`, not `load`, so it never throws; the
 * token wait is already bounded for that (appCheckToken.ts).
 *
 * THE FIX, AT SOURCE. `initializeAppCheck` assigns `onload` and appends the
 * script synchronously, and the load event cannot fire before this function
 * returns. So, straight after the start, the newly appended reCAPTCHA script's
 * `onload` is wrapped: the SDK's callback runs unchanged, and if it throws,
 * the throw is contained and handed to `onUnavailable`, which routes it
 * through the classified App Check path (the fixed
 * `app_check_failure:unavailable`, at most one per page, recorded so Try again
 * reloads). Nothing is filtered by message text; nothing about the SDK's
 * behaviour changes when reCAPTCHA is healthy.
 *
 * If a future SDK version changes how it loads reCAPTCHA (no script appended,
 * no `onload` property), nothing is wrapped and this is a no-op; the unit
 * test against the installed SDK (shared/lib/appCheckStart.test.ts in each
 * app) fails first, so the change is noticed.
 */
export const RECAPTCHA_SCRIPT = /^https:\/\/www\.google\.com\/recaptcha\/(?:api|enterprise)\.js(?:[?#]|$)/;

export function startAppCheckContained(
  start: () => AppCheck,
  onUnavailable: () => void,
  doc: Document = document
): AppCheck {
  const before = new Set(Array.from(doc.getElementsByTagName("script")));
  const appCheck = start();
  for (const script of Array.from(doc.getElementsByTagName("script"))) {
    if (before.has(script) || !RECAPTCHA_SCRIPT.test(script.src)) continue;
    const loaded = script.onload;
    if (typeof loaded !== "function") continue;
    script.onload = function containedRecaptchaLoad(this: GlobalEventHandlers, event: Event) {
      try {
        return loaded.call(this, event);
      } catch (error) {
        // Only the missing-`grecaptcha` case is contained. If reCAPTCHA did
        // define itself, the throw is a genuine reCAPTCHA failure (e.g. a
        // render misconfiguration) and must stay visible.
        if ((globalThis as { grecaptcha?: unknown }).grecaptcha !== undefined) throw error;
        // reCAPTCHA's script ran but defined no `grecaptcha`: App Check
        // cannot attest this page. Never rethrown; classified instead.
        try {
          onUnavailable();
        } catch {
          // Reporting must never become the error.
        }
        return undefined;
      }
    };
  }
  return appCheck;
}
